import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import {
  FeatureDeprecated,
  FeatureActionFields,
  FeatureNames,
  GatewayCommands,
  GatewayProperties,
  IsEnabledProperty,
  OtherRoomCommands,
  OtherRoomProperties,
  RoomCommands,
  RoomProperties,
  Feature,
  IsStringValueProperty,
  ActivateDeactivateCommands,
  RoomsStatusProperties,
  ProgramProperties,
  ProgramCommands,
  DcSensorValueProperties,
  FeaturePropertyKey,
  RoomsCommands,
  DeviceIds,
  RoomOperatingStateProperties,
} from '@vi-iot/client';

import { DOUBLE_DASH } from '@pipes';
import { GTMService } from '@services';
import {
  DevicesStore,
  FeatureService,
  GatewayStore,
  isReady,
  RemoteDeviceType,
  Co2LevelStatus,
  InstallationFeaturesStore,
} from '@store';

import { environment } from 'src/environments/environment';
import { SingleRoomControlOverlayTypeEnum } from '../installation/installation-overlay.model';
import { InstallationService } from '../services/installation.service';
import { PillType } from '../../shared/components/pill/pill.model';
import { CommissioningService } from './src-overlay/commissioning/commissioning.service';

@Component({
  selector: 'app-single-room-control',
  templateUrl: './single-room-control.component.html',
  styleUrls: ['./single-room-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleRoomControlComponent implements OnInit, OnDestroy {
  Co2LevelStatus = Co2LevelStatus;
  DOUBLE_DASH = DOUBLE_DASH;
  roomId: string;
  otherRoomId: string;
  rooms: { [key: string]: Observable<Feature<RoomProperties, RoomsCommands>> } = {};
  rooms$: Observable<Feature<IsEnabledProperty, RoomsCommands>>;
  status$: Observable<Feature<RoomsStatusProperties, ActivateDeactivateCommands>>;
  gatewayStatus$: Observable<Feature<GatewayProperties, GatewayCommands>>;
  firmware$: Observable<Feature<any, any>>; // TODO cannot find it in wiki... ?
  otherRooms$: Observable<Feature<OtherRoomProperties, OtherRoomCommands>[]>;

  ngUnsubscribe: Subject<void> = new Subject<void>();

  readonly singleRoomControlOverlays = SingleRoomControlOverlayTypeEnum;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly featureActionFields = FeatureActionFields;
  readonly viessmannShop = environment.viessmannShop;
  readonly pillType = PillType;

  @Input() installationId: number;
  @Input() deviceSelectId: string;

  constructor(
    public gatewayStore: GatewayStore,
    public devicesStore: DevicesStore,
    protected featureService: FeatureService,
    protected translate: TranslateService,
    protected installationService: InstallationService,
    protected gtmService: GTMService,
    protected commissioningService: CommissioningService,
    protected installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.rooms$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.rooms,
      DeviceIds.RoomControl
    );
    this.status$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomStatus,
      DeviceIds.RoomControl
    );
    this.gatewayStatus$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.gatewayStatus
    );
    this.firmware$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomFirmware,
      DeviceIds.RoomControl
    );
    this.otherRooms$ = this.installationFeaturesStore.otherRooms$;
    this.initRoomsSubscription();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  isExpanded(roomId: string): boolean {
    return this.roomId === roomId;
  }

  expand(roomId: string): void {
    this.roomId = roomId;
    this.otherRoomId = null;
  }

  expandOtherRoom(otherRoomId: string): void {
    this.otherRoomId = otherRoomId;
    this.roomId = null;
  }

  stopEvent(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  openActivationSrcModal(activationSwitch: MatSlideToggle, isChanged: boolean): void {
    if (isChanged) {
      const onMessage = this.translate.instant('+INSTALLATIONS.SRC.TOAST.SUCCESS.SRC_ON');
      const offMessage = this.translate.instant('+INSTALLATIONS.SRC.TOAST.SUCCESS.SRC_OFF');
      this.installationFeaturesStore.updateFeature(
        FeatureNames.roomStatus,
        !activationSwitch.checked,
        'active',
        '',
        !activationSwitch.checked ? 'activate' : 'deactivate',
        null,
        DeviceIds.RoomControl,
        {
          successMessage: !activationSwitch.checked ? onMessage : offMessage,
        }
      );
      this.onSrcActivationSwitchChange(activationSwitch);
    }

    this.gtmService.track(
      isChanged ? 'onConfirm' : 'onCancel',
      'Toggle single room control',
      'singleRoomControl'
    );
  }

  onSrcActivationSwitchChange(srcActivationSwitch: MatSlideToggle): void {
    srcActivationSwitch.toggle();
  }

  isAnyUnAssignableDevice(groupOfDevices: { type: string; devices: any[] }[]): boolean {
    return groupOfDevices.some((group) => !!group.devices.length);
  }

  getRoom(roomId: string): Observable<Feature<RoomProperties, RoomCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomDetails(roomId),
      DeviceIds.RoomControl
    );
  }

  getChildLockForRoom(
    roomId: string
  ): Observable<Feature<IsStringValueProperty, ActivateDeactivateCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.childLock(roomId),
      DeviceIds.RoomControl
    );
  }

  getRoomOperatingProgram(roomId: string): Observable<Feature<ProgramProperties, ProgramCommands>> {
    return this.getRoomActiveOperatingProgram(roomId).pipe(
      takeUntil(this.ngUnsubscribe),
      filter((feature) => isReady(feature)),
      filter((feature) => !!feature?.properties?.level?.value),
      map((feature) => feature.properties.level.value),
      switchMap((activeProgram) => {
        const srcOperatingProgram = this.featureService.getSRCOperatingProgramHeating(
          activeProgram
        );

        return this.installationFeaturesStore.selectInstallationDeviceFeature<
          ProgramProperties,
          ProgramCommands
        >(
          FeatureNames.srcOperatingProgram(roomId, srcOperatingProgram, false),
          DeviceIds.RoomControl
        );
      })
    );
  }

  getRoomTemperature(roomId: string): Observable<Number> {
    return this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.roomTemperature(roomId), DeviceIds.RoomControl)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(
          (feature: Feature<DcSensorValueProperties, never>) =>
            isReady(feature) && !!feature.properties?.value?.value
        ),
        map((feature: Feature<DcSensorValueProperties, never>) => feature.properties.value.value)
      );
  }

  getOtherRoomActiveProgram(
    roomId: string
  ): Observable<Feature<ProgramProperties, ProgramCommands>> {
    return this.getOtherRoomActiveOperatingProgram(roomId).pipe(
      takeUntil(this.ngUnsubscribe),
      filter((feature) => isReady(feature)),
      filter((feature) => !!feature?.properties?.level?.value),
      map((feature) => feature.properties.level.value),
      switchMap((activeProgram) => {
        const srcOperatingProgram = this.featureService.getSRCOperatingProgramHeating(
          activeProgram
        );

        return this.installationFeaturesStore.selectInstallationDeviceFeature<
          ProgramProperties,
          ProgramCommands
        >(
          FeatureNames.srcOperatingProgram(roomId, srcOperatingProgram, true),
          DeviceIds.RoomControl
        );
      })
    );
  }

  canShowRoomTemperature(
    roomItem: FeatureDeprecated<RoomProperties, RoomCommands>
  ): Observable<boolean> {
    return this.gatewayStore.isInRoomTRVInMountingMode(roomItem);
  }

  isDeviceFHTChannel(deviceId: string): Observable<boolean> {
    return this.gatewayStore.isDeviceOfType(deviceId, RemoteDeviceType.FHT_CHANNEL);
  }

  private initRoomsSubscription(): void {
    this.rooms$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => isReady(feature)),
        filter((feature) => !!feature?.properties?.enabled?.value?.length),
        map((feature) => feature.properties.enabled.value)
      )
      .subscribe((rooms) => {
        rooms.forEach((roomId) => {
          this.rooms[roomId] = this.installationFeaturesStore.selectInstallationDeviceFeature(
            FeatureNames.roomDetails(roomId),
            DeviceIds.RoomControl
          );
        });
      });
  }

  private getRoomActiveOperatingProgram(
    roomId: string
  ): Observable<Feature<RoomOperatingStateProperties, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomOperatingState(roomId),
      DeviceIds.RoomControl
    );
  }

  private getOtherRoomActiveOperatingProgram(
    roomId: string
  ): Observable<Feature<RoomOperatingStateProperties, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.otherRoomOperatingState(roomId),
      DeviceIds.RoomControl
    );
  }
}
